'use client';

import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from '@/hooks/useTranslation';
import { useRTL } from '@/hooks/useRTL';
import { cn } from '@/lib/utils';
import {
    ChevronLeft,
    ChevronRight,
    User,
    Calendar,
    Stethoscope,
    MessageSquare,
    CheckCircle,
    Loader2,
    Clock,
    Phone,
    Mail,
    MapPin
} from 'lucide-react';

// Form validation schema - Note: These are kept in English as they are internal validation messages
// User-facing error messages should use translations
const formSchema = z.object({
    // Personal Information
    firstName: z.string().min(2, 'First name is required'),
    lastName: z.string().min(2, 'Last name is required'),
    email: z.string().email('Invalid email address'),
    phone: z.string().min(5, 'Phone number is required'),
    age: z.number().min(1, 'Age is required').max(120, 'Please enter a valid age'),
    country: z.string().min(2, 'Please select a country'),

    // Appointment Type
    appointmentType: z.enum(['consultation', 'treatment'], {
        message: 'Please select an appointment type',
    }),

    // Treatments
    treatments: z.array(z.string()).min(1, 'Please select at least one treatment'),

    // Additional Information
    description: z.string().optional(),
    preferredDate: z.string().optional(),
    preferredTime: z.string().optional(),
});

type FormValues = z.infer<typeof formSchema>;

// List of countries
const countries = [
    'Afghanistan', 'Albania', 'Algeria', 'Andorra', 'Angola', 'Argentina', 'Armenia', 'Australia',
    'Austria', 'Azerbaijan', 'Bahamas', 'Bahrain', 'Bangladesh', 'Barbados', 'Belarus', 'Belgium',
    'Belize', 'Benin', 'Bhutan', 'Bolivia', 'Bosnia and Herzegovina', 'Botswana', 'Brazil',
    'Brunei', 'Bulgaria', 'Burkina Faso', 'Burundi', 'Cambodia', 'Cameroon', 'Canada',
    'Cape Verde', 'Central African Republic', 'Chad', 'Chile', 'China', 'Colombia', 'Comoros',
    'Congo', 'Costa Rica', 'Croatia', 'Cuba', 'Cyprus', 'Czech Republic', 'Denmark', 'Djibouti',
    'Dominica', 'Dominican Republic', 'Ecuador', 'Egypt', 'El Salvador', 'Equatorial Guinea',
    'Eritrea', 'Estonia', 'Eswatini', 'Ethiopia', 'Fiji', 'Finland', 'France', 'Gabon',
    'Gambia', 'Georgia', 'Germany', 'Ghana', 'Greece', 'Grenada', 'Guatemala', 'Guinea',
    'Guinea-Bissau', 'Guyana', 'Haiti', 'Honduras', 'Hungary', 'Iceland', 'India', 'Indonesia',
    'Iran', 'Iraq', 'Ireland', 'Israel', 'Italy', 'Jamaica', 'Japan', 'Jordan', 'Kazakhstan',
    'Kenya', 'Kiribati', 'Kuwait', 'Kyrgyzstan', 'Laos', 'Latvia', 'Lebanon', 'Lesotho',
    'Liberia', 'Libya', 'Liechtenstein', 'Lithuania', 'Luxembourg', 'Madagascar', 'Malawi',
    'Malaysia', 'Maldives', 'Mali', 'Malta', 'Marshall Islands', 'Mauritania', 'Mauritius',
    'Mexico', 'Micronesia', 'Moldova', 'Monaco', 'Mongolia', 'Montenegro', 'Morocco',
    'Mozambique', 'Myanmar', 'Namibia', 'Nauru', 'Nepal', 'Netherlands', 'New Zealand',
    'Nicaragua', 'Niger', 'Nigeria', 'North Korea', 'North Macedonia', 'Norway', 'Oman',
    'Pakistan', 'Palau', 'Palestine', 'Panama', 'Papua New Guinea', 'Paraguay', 'Peru',
    'Philippines', 'Poland', 'Portugal', 'Qatar', 'Romania', 'Russia', 'Rwanda',
    'Saint Kitts and Nevis', 'Saint Lucia', 'Saint Vincent and the Grenadines', 'Samoa',
    'San Marino', 'Sao Tome and Principe', 'Saudi Arabia', 'Senegal', 'Serbia', 'Seychelles',
    'Sierra Leone', 'Singapore', 'Slovakia', 'Slovenia', 'Solomon Islands', 'Somalia',
    'South Africa', 'South Korea', 'South Sudan', 'Spain', 'Sri Lanka', 'Sudan', 'Suriname',
    'Sweden', 'Switzerland', 'Syria', 'Taiwan', 'Tajikistan', 'Tanzania', 'Thailand',
    'Timor-Leste', 'Togo', 'Tonga', 'Trinidad and Tobago', 'Tunisia', 'Turkey', 'Turkmenistan',
    'Tuvalu', 'Uganda', 'Ukraine', 'United Arab Emirates', 'United Kingdom', 'United States',
    'Uruguay', 'Uzbekistan', 'Vanuatu', 'Vatican City', 'Venezuela', 'Vietnam', 'Yemen',
    'Zambia', 'Zimbabwe'
];

const treatments = [
    {
        id: 'dental-implants',
        name: 'Dental Implants',
        category: 'Restorative',
        icon: '/images/icons/booking-icons/implant.webp'
    },
    {
        id: 'dental-veneers',
        name: 'Dental Veneers',
        category: 'Cosmetic',
        icon: '/images/icons/booking-icons/veneer.webp'
    },
    {
        id: 'teeth-whitening',
        name: 'Teeth Whitening',
        category: 'Cosmetic',
        icon: '/images/icons/booking-icons/whitening.webp'
    },
    {
        id: 'hollywood-smile',
        name: 'Hollywood Smile',
        category: 'Cosmetic',
        icon: '/images/icons/booking-icons/hollywood-smile.webp'
    },
    {
        id: 'all-on-four',
        name: 'All-on-4 Implants',
        category: 'Restorative',
        icon: '/images/icons/booking-icons/dental.webp'
    },
    {
        id: 'root-canal',
        name: 'Root Canal Treatment',
        category: 'Endodontic',
        icon: '/images/icons/booking-icons/root-canal.webp'
    },
];


// Calendar component
function CalendarPicker({ selectedDate, onDateSelect, selectedTime, onTimeSelect, t, isRTL }: {
    selectedDate: string;
    onDateSelect: (date: string) => void;
    selectedTime: string;
    onTimeSelect: (time: string) => void;
    t: any;
    isRTL: boolean;
}) {
    const [currentMonth, setCurrentMonth] = useState(new Date());

    const today = new Date();
    const daysInMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0).getDate();
    const firstDayOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1).getDay();

    const timeSlots = [
        '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
        '14:00', '14:30', '15:00', '15:30', '16:00', '16:30',
        '17:00', '17:30', '18:00', '18:30'
    ];

    const monthNames = t('form.calendar.months') || [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];

    const isDateDisabled = (day: number) => {
        const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
        return date < today || date.getDay() === 0; // Disable past dates and Sundays
    };

    const formatDateForInput = (day: number) => {
        const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
        return date.toISOString().split('T')[0];
    };

    return (
        <div className="grid md:grid-cols-2 gap-4">
            {/* Calendar */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3">
                <div className="flex items-center justify-between mb-3">
                    <button
                        type="button"
                        onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
                        className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-sm text-gray-700 dark:text-gray-300"
                    >
                        <ChevronLeft className={cn("w-4 h-4", isRTL && "rotate-180")} />
                    </button>
                    <h3 className="font-semibold text-sm text-gray-900 dark:text-gray-100">
                        {Array.isArray(monthNames) ? monthNames[currentMonth.getMonth()] : monthNames} {currentMonth.getFullYear()}
                    </h3>
                    <button
                        type="button"
                        onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
                        className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-sm text-gray-700 dark:text-gray-300"
                    >
                        <ChevronRight className={cn("w-4 h-4", isRTL && "rotate-180")} />
                    </button>
                </div>

                <div className="grid grid-cols-7 gap-1 mb-2">
                    {(t('form.calendar.days') || ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']).map((day: string) => (
                        <div key={day} className="text-center text-xs font-medium text-gray-500 dark:text-gray-400 p-1">
                            {day}
                        </div>
                    ))}
                </div>

                <div className="grid grid-cols-7 gap-1">
                    {Array.from({ length: firstDayOfMonth }, (_, i) => (
                        <div key={`empty-${i}`} className="p-1"></div>
                    ))}
                    {Array.from({ length: daysInMonth }, (_, i) => {
                        const day = i + 1;
                        const dateStr = formatDateForInput(day);
                        const isSelected = selectedDate === dateStr;
                        const isDisabled = isDateDisabled(day);

                        return (
                            <button
                                key={day}
                                type="button"
                                onClick={() => !isDisabled && onDateSelect(dateStr)}
                                disabled={isDisabled}
                                className={`p-1 text-xs rounded transition-colors ${isSelected
                                    ? 'bg-blue-900 text-yellow-400 shadow-md ring-2 ring-yellow-400/50'
                                    : isDisabled
                                        ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                                        : 'text-gray-700 dark:text-gray-300 hover:bg-blue-50 dark:hover:bg-gray-700 hover:bg-primary/10 dark:hover:bg-primary/20'
                                    }`}
                            >
                                {day}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Time Slots */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3">
                <h3 className="font-semibold mb-3 flex items-center text-sm text-gray-900 dark:text-gray-100">
                    <Clock className="w-4 h-4 mr-2" />
                    {t('form.calendar.availableTimes')}
                </h3>
                <div className="grid grid-cols-2 gap-1">
                    {timeSlots.map(time => (
                        <button
                            key={time}
                            type="button"
                            onClick={() => onTimeSelect(time)}
                            className={`px-2 py-1 text-xs rounded border transition-colors ${selectedTime === time
                                ? 'bg-blue-900 text-yellow-400 border-yellow-400 shadow-md ring-2 ring-yellow-400/50'
                                : 'text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-600 hover:bg-primary/10 dark:hover:bg-primary/20 hover:border-primary/50 dark:hover:border-primary/50'
                                }`}
                        >
                            {time}
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
}

export default function BookingForm() {
    const { t } = useTranslation('booking');
    const { isRTL } = useRTL();
    const [isMounted, setIsMounted] = useState(false);
    const [currentStep, setCurrentStep] = useState(1);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [submittedData, setSubmittedData] = useState<FormValues | null>(null);
    const { toast } = useToast();

    // Define steps with translations
    const steps = [
        { id: 1, title: t('form.steps.personalInfo.title'), icon: User, description: t('form.steps.personalInfo.description') },
        { id: 2, title: t('form.steps.appointmentType.title'), icon: Calendar, description: t('form.steps.appointmentType.description') },
        { id: 3, title: t('form.steps.treatments.title'), icon: Stethoscope, description: t('form.steps.treatments.description') },
        { id: 4, title: t('form.steps.scheduleDetails.title'), icon: MessageSquare, description: t('form.steps.scheduleDetails.description') },
    ];

    // Fix hydration mismatch caused by browser extensions
    useEffect(() => {
        setIsMounted(true);
    }, []);

    const {
        register,
        watch,
        setValue,
        trigger,
        reset,
        formState: { errors },
    } = useForm<FormValues>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            treatments: [],
            appointmentType: 'consultation',
        },
    });

    const watchedTreatments = watch('treatments') || [];
    const watchedAppointmentType = watch('appointmentType');
    const watchedPreferredDate = watch('preferredDate') || '';
    const watchedPreferredTime = watch('preferredTime') || '';

    const nextStep = async () => {
        let fieldsToValidate: (keyof FormValues)[] = [];

        switch (currentStep) {
            case 1:
                fieldsToValidate = ['firstName', 'lastName', 'email', 'phone', 'age', 'country'];
                break;
            case 2:
                fieldsToValidate = ['appointmentType'];
                break;
            case 3:
                fieldsToValidate = ['treatments'];
                break;
        }

        const isValid = await trigger(fieldsToValidate);
        if (isValid && currentStep < steps.length) {
            setCurrentStep(currentStep + 1);
        }
    };

    const prevStep = () => {
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1);
        }
    };

    const toggleTreatment = (treatmentId: string) => {
        const currentTreatments = watchedTreatments;
        const updatedTreatments = currentTreatments.includes(treatmentId)
            ? currentTreatments.filter(id => id !== treatmentId)
            : [...currentTreatments, treatmentId];

        setValue('treatments', updatedTreatments);
    };

    const onSubmit = async (data: FormValues) => {
        setIsSubmitting(true);

        try {
            // Ensure submission data is in English for backend processing
            const submissionData = {
                ...data,
                // Keep original IDs for treatments as they are already in English
                treatments: data.treatments,
                // Keep appointment type as English enum values
                appointmentType: data.appointmentType,
                // Add language info for reference
                submissionLanguage: 'en',
                userLanguage: document.documentElement.lang || 'en'
            };
            
            const response = await fetch('/api/booking', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(submissionData),
            });

            if (!response.ok) {
                throw new Error('Failed to submit booking');
            }

            setSubmittedData(data);
            setIsSuccess(true);
        } catch (error) {
            toast({
                title: t('errors.submitFailed'),
                description: t('errors.submitFailedDescription'),
                variant: 'destructive',
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleManualSubmit = async () => {
        // Trigger form validation
        const isValid = await trigger();
        if (isValid) {
            const formData = watch();
            await onSubmit(formData);
        }
    };

    const handleNewBooking = () => {
        setIsSuccess(false);
        setSubmittedData(null);
        reset();
        setCurrentStep(1);
    };

    const renderStepContent = () => {
        switch (currentStep) {
            case 1:
                return (
                    <div className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium mb-2">{t('form.fields.firstName.label')}</label>
                                <Input
                                    {...register('firstName')}
                                    placeholder={t('form.fields.firstName.placeholder')}
                                    className={`h-12 text-base ${errors.firstName ? 'border-red-500' : ''}`}
                                />
                                {errors.firstName && (
                                    <p className="text-red-500 text-sm mt-1">{t('form.errors.firstName') || errors.firstName.message}</p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-2">{t('form.fields.lastName.label')}</label>
                                <Input
                                    {...register('lastName')}
                                    placeholder={t('form.fields.lastName.placeholder')}
                                    className={`h-12 text-base ${errors.lastName ? 'border-red-500' : ''}`}
                                />
                                {errors.lastName && (
                                    <p className="text-red-500 text-sm mt-1">{t('form.errors.lastName') || errors.lastName.message}</p>
                                )}
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium mb-2">{t('form.fields.email.label')}</label>
                            <Input
                                {...register('email')}
                                type="email"
                                placeholder={t('form.fields.email.placeholder')}
                                className={`h-12 text-base ${errors.email ? 'border-red-500' : ''}`}
                            />
                            {errors.email && (
                                <p className="text-red-500 text-sm mt-1">{t('form.errors.email') || errors.email.message}</p>
                            )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium mb-2">{t('form.fields.phone.label')}</label>
                                <Input
                                    {...register('phone')}
                                    placeholder={t('form.fields.phone.placeholder')}
                                    className={`h-12 text-base ${errors.phone ? 'border-red-500' : ''}`}
                                />
                                {errors.phone && (
                                    <p className="text-red-500 text-sm mt-1">{t('form.errors.phone') || errors.phone.message}</p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-2">{t('form.fields.age.label')}</label>
                                <Input
                                    {...register('age', { valueAsNumber: true })}
                                    type="number"
                                    min="1"
                                    max="120"
                                    placeholder={t('form.fields.age.placeholder')}
                                    className={`h-12 text-base ${errors.age ? 'border-red-500' : ''}`}
                                />
                                {errors.age && (
                                    <p className="text-red-500 text-sm mt-1">{t('form.errors.age') || errors.age.message}</p>
                                )}
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium mb-2">{t('form.fields.country.label')}</label>
                            <select
                                {...register('country')}
                                className={`w-full h-12 text-base p-3 border rounded-sm ${errors.country ? 'border-red-500' : ''}`}
                            >
                                <option value="">{t('form.fields.country.placeholder')}</option>
                                {countries.map((country) => (
                                    <option key={country} value={country}>
                                        {country}
                                    </option>
                                ))}
                            </select>
                            {errors.country && (
                                <p className="text-red-500 text-sm mt-1">{t('form.errors.country') || errors.country.message}</p>
                            )}
                        </div>
                    </div>
                );

            case 2:
                return (
                    <div className="space-y-6">
                        <div className="text-center mb-8">
                            <h3 className="text-xl font-semibold mb-2">{t('form.appointmentTypes.title')}</h3>
                            <p className="text-muted-foreground">{t('form.appointmentTypes.subtitle')}</p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <motion.div
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                            >
                                <Card
                                    className={`cursor-pointer transition-all ${watchedAppointmentType === 'consultation'
                                        ? 'ring-2 ring-primary bg-primary/5'
                                        : 'hover:shadow-md'
                                        }`}
                                    onClick={() => setValue('appointmentType', 'consultation')}
                                >
                                    <CardContent className="p-6 text-center">
                                        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                            <Calendar className="w-8 h-8 text-blue-600" />
                                        </div>
                                        <h4 className="text-lg font-semibold mb-2">{t('form.appointmentTypes.consultation.title')}</h4>
                                        <p className="text-muted-foreground text-sm">
                                            {t('form.appointmentTypes.consultation.description')}
                                        </p>
                                        {watchedAppointmentType === 'consultation' && (
                                            <CheckCircle className="w-6 h-6 text-primary mx-auto mt-3" />
                                        )}
                                    </CardContent>
                                </Card>
                            </motion.div>

                            <motion.div
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                            >
                                <Card
                                    className={`cursor-pointer transition-all ${watchedAppointmentType === 'treatment'
                                        ? 'ring-2 ring-primary bg-primary/5'
                                        : 'hover:shadow-md'
                                        }`}
                                    onClick={() => setValue('appointmentType', 'treatment')}
                                >
                                    <CardContent className="p-6 text-center">
                                        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                            <Stethoscope className="w-8 h-8 text-green-600" />
                                        </div>
                                        <h4 className="text-lg font-semibold mb-2">{t('form.appointmentTypes.treatment.title')}</h4>
                                        <p className="text-muted-foreground text-sm">
                                            {t('form.appointmentTypes.treatment.description')}
                                        </p>
                                        {watchedAppointmentType === 'treatment' && (
                                            <CheckCircle className="w-6 h-6 text-primary mx-auto mt-3" />
                                        )}
                                    </CardContent>
                                </Card>
                            </motion.div>
                        </div>

                        {errors.appointmentType && (
                            <p className="text-red-500 text-sm text-center">{t('form.errors.appointmentType') || errors.appointmentType.message}</p>
                        )}
                    </div>
                );

            case 3:
                return (
                    <div className="space-y-6">
                        <div className="text-center mb-6">
                            <h3 className="text-xl font-semibold mb-2">{t('form.treatments.title')}</h3>
                            <p className="text-muted-foreground">{t('form.treatments.subtitle')}</p>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                            {treatments.map((treatment) => (
                                <motion.div
                                    key={treatment.id}
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                >
                                    <Card
                                        className={`cursor-pointer transition-all h-full ${watchedTreatments.includes(treatment.id)
                                            ? 'ring-2 ring-primary bg-primary/5'
                                            : 'hover:shadow-md hover:bg-gray-50'
                                            }`}
                                        onClick={() => toggleTreatment(treatment.id)}
                                    >
                                        <CardContent className="p-4 text-center">
                                            <div className="w-12 h-12 mx-auto mb-3 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center">
                                                <img
                                                    src={treatment.icon}
                                                    alt={treatment.name}
                                                    className="w-8 h-8 object-contain"
                                                />
                                            </div>
                                            <div className="text-xs font-medium text-primary mb-1">{t(`form.treatments.categories.${treatment.category.toLowerCase()}`) || treatment.category}</div>
                                            <div className="text-sm font-semibold leading-tight">{t(`form.treatments.items.${treatment.id}.name`) || treatment.name}</div>
                                            {watchedTreatments.includes(treatment.id) && (
                                                <CheckCircle className="w-4 h-4 text-primary mx-auto mt-2" />
                                            )}
                                        </CardContent>
                                    </Card>
                                </motion.div>
                            ))}
                        </div>

                        {watchedTreatments.length > 0 && (
                            <div className="mt-6 p-4 bg-primary/5 rounded-lg">
                                <h4 className="font-medium mb-3 text-center">{t('form.treatments.selected')}:</h4>
                                <div className="flex flex-wrap gap-2 justify-center">
                                    {watchedTreatments.map((treatmentId) => {
                                        const treatment = treatments.find(t => t.id === treatmentId);
                                        return (
                                            <Badge key={treatmentId} variant="secondary" className="px-3 py-1">
                                                {t(`form.treatments.items.${treatmentId}.name`) || treatment?.name}
                                            </Badge>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {errors.treatments && (
                            <p className="text-red-500 text-sm text-center">{t('form.errors.treatments') || errors.treatments.message}</p>
                        )}
                    </div>
                );

            case 4:
                return (
                    <div className="space-y-6">
                        <div className="text-center mb-6">
                            <h3 className="text-xl font-semibold mb-2">{t('form.schedule.title')}</h3>
                            <p className="text-muted-foreground">{t('form.schedule.subtitle')}</p>
                        </div>

                        <CalendarPicker
                            selectedDate={watchedPreferredDate}
                            onDateSelect={(date) => setValue('preferredDate', date)}
                            selectedTime={watchedPreferredTime}
                            onTimeSelect={(time) => setValue('preferredTime', time)}
                            t={t}
                            isRTL={isRTL}
                        />

                        {(watchedPreferredDate || watchedPreferredTime) && (
                            <div className="p-4 bg-primary/5 rounded-lg text-center">
                                <h4 className="font-medium mb-2">{t('form.schedule.selectedAppointment')}:</h4>
                                <p className="text-primary font-semibold">
                                    {watchedPreferredDate && new Date(watchedPreferredDate).toLocaleDateString('en-US', {
                                        weekday: 'long',
                                        year: 'numeric',
                                        month: 'long',
                                        day: 'numeric'
                                    })}
                                    {watchedPreferredTime && ` at ${watchedPreferredTime}`}
                                </p>
                            </div>
                        )}

                        <div>
                            <label className="block text-sm font-medium mb-2">
                                {t('form.fields.description.label')}
                            </label>
                            <Textarea
                                {...register('description')}
                                placeholder={t('form.fields.description.placeholder')}
                                rows={2}
                                className={errors.description ? 'border-red-500' : ''}
                            />
                            {errors.description && (
                                <p className="text-red-500 text-sm mt-1">{t('form.errors.description') || errors.description.message}</p>
                            )}
                        </div>
                    </div>
                );

            default:
                return null;
        }
    };

    const renderSuccessMessage = () => {
        return (
            <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
                className="space-y-8 p-6 md:p-8 max-w-4xl mx-auto"
            >
                {/* Success Icon */}
                <div className="text-center">
                    <div className="mx-auto w-20 h-20 md:w-24 md:h-24 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-4">
                        <CheckCircle className="w-10 h-10 md:w-12 md:h-12 text-green-600 dark:text-green-400" />
                    </div>
                    <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                        {t('success.title')}
                    </h2>
                    <p className="text-base md:text-lg text-gray-600 dark:text-gray-400">
                        {t('success.subtitle')}
                    </p>
                </div>

                {/* Appointment Details */}
                {submittedData && (
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                        <h3 className="text-lg font-semibold mb-6 text-center text-gray-900 dark:text-gray-100">
                            {t('success.details.title')}
                        </h3>

                        <div className="grid gap-4 sm:grid-cols-2">
                            <div className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                                <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                                    <User className="w-5 h-5 text-primary" />
                                </div>
                                <div className="min-w-0">
                                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('success.details.patient')}</p>
                                    <p className="font-semibold text-gray-900 dark:text-gray-100 truncate">
                                        {submittedData.firstName} {submittedData.lastName}
                                    </p>
                                </div>
                            </div>

                            <div className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                                <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                                    <Mail className="w-5 h-5 text-primary" />
                                </div>
                                <div className="min-w-0">
                                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('success.details.email')}</p>
                                    <p className="font-semibold text-gray-900 dark:text-gray-100 truncate">
                                        {submittedData.email}
                                    </p>
                                </div>
                            </div>

                            <div className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                                <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                                    <Phone className="w-5 h-5 text-primary" />
                                </div>
                                <div className="min-w-0">
                                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('success.details.phone')}</p>
                                    <p className="font-semibold text-gray-900 dark:text-gray-100">
                                        {submittedData.phone}
                                    </p>
                                </div>
                            </div>

                            <div className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                                <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                                    <Calendar className="w-5 h-5 text-primary" />
                                </div>
                                <div className="min-w-0">
                                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('success.details.type')}</p>
                                    <p className="font-semibold text-gray-900 dark:text-gray-100 capitalize">
                                        {t(`form.appointmentTypes.${submittedData.appointmentType}.title`) || submittedData.appointmentType}
                                    </p>
                                </div>
                            </div>

                            {submittedData.preferredDate && (
                                <div className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg sm:col-span-2">
                                    <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                                        <Clock className="w-5 h-5 text-primary" />
                                    </div>
                                    <div className="min-w-0">
                                        <p className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('success.details.preferredDateTime')}</p>
                                        <p className="font-semibold text-gray-900 dark:text-gray-100">
                                            {new Date(submittedData.preferredDate).toLocaleDateString('en-US', {
                                                weekday: 'long',
                                                year: 'numeric',
                                                month: 'long',
                                                day: 'numeric'
                                            })}
                                            {submittedData.preferredTime && ` at ${submittedData.preferredTime}`}
                                        </p>
                                    </div>
                                </div>
                            )}

                            {submittedData.treatments.length > 0 && (
                                <div className="sm:col-span-2 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3">{t('success.details.selectedTreatments')}</p>
                                    <div className="flex flex-wrap gap-2">
                                        {submittedData.treatments.map((treatmentId) => {
                                            const treatment = treatments.find(t => t.id === treatmentId);
                                            return (
                                                <Badge key={treatmentId} variant="secondary" className="text-xs px-2 py-1">
                                                    {t(`form.treatments.items.${treatmentId}.name`) || treatment?.name}
                                                </Badge>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Next Steps */}
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800 p-6">
                    <h3 className="text-lg font-semibold mb-6 text-center text-blue-900 dark:text-blue-100">
                        {t('success.nextSteps.title')}
                    </h3>
                    <div className="space-y-6">
                        <div className="flex items-start space-x-4">
                            <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">
                                1
                            </div>
                            <div className="flex-1">
                                <h4 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">
                                    {t('success.nextSteps.step1.title')}
                                </h4>
                                <p className="text-blue-700 dark:text-blue-300 text-sm leading-relaxed">
                                    {t('success.nextSteps.step1.description')}
                                </p>
                            </div>
                        </div>

                        <div className="flex items-start space-x-4">
                            <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">
                                2
                            </div>
                            <div className="flex-1">
                                <h4 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">
                                    {t('success.nextSteps.step2.title')}
                                </h4>
                                <p className="text-blue-700 dark:text-blue-300 text-sm leading-relaxed">
                                    {t('success.nextSteps.step2.description')}
                                </p>
                            </div>
                        </div>

                        <div className="flex items-start space-x-4">
                            <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">
                                3
                            </div>
                            <div className="flex-1">
                                <h4 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">
                                    {t('success.nextSteps.step3.title')}
                                </h4>
                                <p className="text-blue-700 dark:text-blue-300 text-sm leading-relaxed">
                                    {t('success.nextSteps.step3.description')}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Contact Information */}
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                    <h3 className="text-lg font-semibold mb-6 text-center text-gray-900 dark:text-gray-100">
                        {t('success.contact.title')}
                    </h3>
                    <div className="grid gap-4 sm:grid-cols-2">
                        <div className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                            <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                                <Phone className="w-5 h-5 text-primary" />
                            </div>
                            <div className="min-w-0">
                                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('success.contact.mobile')}</p>
                                <p className="font-semibold text-gray-900 dark:text-gray-100">
                                    +90 507 080 8888
                                </p>
                            </div>
                        </div>

                        <div className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                            <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                                <Phone className="w-5 h-5 text-primary" />
                            </div>
                            <div className="min-w-0">
                                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('success.contact.office')}</p>
                                <p className="font-semibold text-gray-900 dark:text-gray-100">
                                    +90 212 533 8888
                                </p>
                            </div>
                        </div>

                        <div className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                            <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                                <Mail className="w-5 h-5 text-primary" />
                            </div>
                            <div className="min-w-0">
                                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Email</p>
                                <p className="font-semibold text-gray-900 dark:text-gray-100">
                                    info@dentawista.com
                                </p>
                            </div>
                        </div>

                        <div className="flex items-start space-x-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                            <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                <MapPin className="w-5 h-5 text-primary" />
                            </div>
                            <div className="min-w-0">
                                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('success.contact.address')}</p>
                                <p className="font-semibold text-gray-900 dark:text-gray-100 text-sm leading-relaxed">
                                    Merkez, Cendere Cad. No:9, 34406 Kağıthane/İstanbul, Türkiye
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
                    <Button
                        onClick={handleNewBooking}
                        variant="outline"
                        className="order-2 sm:order-1 px-6 py-3 text-base"
                    >
                        {t('success.actions.bookAnother')}
                    </Button>
                    <Button
                        onClick={() => window.location.href = '/'}
                        className="order-1 sm:order-2 px-6 py-3 text-base"
                    >
                        {t('success.actions.returnHome')}
                    </Button>
                </div>
            </motion.div>
        );
    };

    // Prevent hydration mismatch by only rendering form after mount
    if (!isMounted) {
        return (
            <Card className="w-full max-w-4xl mx-auto">
                <CardContent className="p-8">
                    <div className="flex items-center justify-center min-h-[500px]">
                        <div className="text-center">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                            <p className="text-muted-foreground">{t('form.loading') || 'Loading booking form...'}</p>
                        </div>
                    </div>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card className="w-full max-w-4xl mx-auto">
            {!isSuccess ? (
                <>
                    <CardHeader>
                        <CardTitle className="text-center">{t('form.title')}</CardTitle>

                        {/* Progress Steps */}
                        <div className="flex justify-between items-center mt-8">
                            {steps.map((step, index) => (
                                <div key={step.id} className="flex items-center">
                                    <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 transition-colors ${currentStep >= step.id
                                        ? 'bg-gray-600 border-gray-600 text-white shadow-lg'
                                        : 'border-gray-300 dark:border-gray-600 text-gray-400 dark:text-gray-500 bg-white dark:bg-gray-800'
                                        }`}>
                                        {currentStep > step.id ? (
                                            <CheckCircle className="w-5 h-5" />
                                        ) : (
                                            <step.icon className="w-5 h-5" />
                                        )}
                                    </div>
                                    <div className="ml-3 hidden md:block">
                                        <p className={`text-sm font-medium ${currentStep >= step.id ? 'text-primary' : 'text-gray-400'
                                            }`}>
                                            {step.title}
                                        </p>
                                        <p className="text-xs text-muted-foreground">{step.description}</p>
                                    </div>
                                    {index < steps.length - 1 && (
                                        <div className={`w-12 md:w-12 h-0.5 mx-1 md:mx-4 ${currentStep > step.id ? 'bg-primary' : 'bg-gray-300'
                                            }`} />
                                    )}
                                </div>
                            ))}
                        </div>
                    </CardHeader>

                    <CardContent>
                        <div>
                            <AnimatePresence mode="wait">
                                <motion.div
                                    key={currentStep}
                                    initial={{ opacity: 0, x: 20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: -20 }}
                                    transition={{ duration: 0.3 }}
                                    className="min-h-[550px] flex flex-col justify-start"
                                >
                                    {renderStepContent()}
                                </motion.div>
                            </AnimatePresence>

                            {/* Navigation Buttons */}
                            <div className="flex justify-between mt-8">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={prevStep}
                                    disabled={currentStep === 1}
                                    className="flex items-center"
                                >
                                    <ChevronLeft className={cn("w-4 h-4 mr-2", isRTL && "rotate-180")} />
                                    {t('form.navigation.previous')}
                                </Button>

                                {currentStep < steps.length ? (
                                    <Button
                                        type="button"
                                        onClick={nextStep}
                                        className="flex items-center"
                                    >
                                        {t('form.navigation.next')}
                                        <ChevronRight className={cn("w-4 h-4 ml-2", isRTL && "rotate-180")} />
                                    </Button>
                                ) : (
                                    <Button
                                        type="button"
                                        onClick={handleManualSubmit}
                                        disabled={isSubmitting}
                                        className="flex items-center"
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                {t('form.navigation.submitting')}
                                            </>
                                        ) : (
                                            t('form.navigation.submit')
                                        )}
                                    </Button>
                                )}
                            </div>
                        </div>
                    </CardContent>
                </>
            ) : (
                <CardContent>
                    {renderSuccessMessage()}
                </CardContent>
            )}
        </Card>
    );
}
